import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import { BellIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import { useQuery } from '@tanstack/react-query'

import { useModalManager } from '../../lib/hooks/use-modal-manager'
import {
  StaffReminder,
  StaffRemindersHistory,
  StaffReminderType
} from '../../lib/types/models/staff/staff-reminder'
import {
  getStaffReminderLabel,
  getStaffReminderSenderLabel,
  getStaffReminderTypeLabel,
  getStaffReminderVariant
} from '../../lib/utils/functions/staff-reminder.functions'

const historyTitles: Record<StaffReminderType, string> = {
  CONTRACT: 'Historique des relances contrat',
  MISSING_DOCUMENTS: 'Historique des relances documents'
}

type Props = {
  reminders?: StaffReminder[]
  type: StaffReminderType
  /** Unique key of the row's history, e.g. ['affected-staff-reminders', id] */
  historyQueryKey: (string | number)[]
  /** Fetches the row's reminder history (with each sender) once the popup is opened */
  getHistory: () => Promise<StaffRemindersHistory>
  /** In a narrow column, lets the badge break after the dash, on 2 lines */
  wrap?: boolean
}

/** Latest reminder only (grey friendly, red final), its full history in a popup on click. */
const StaffLatestReminderCell = ({
  reminders,
  type,
  historyQueryKey,
  getHistory,
  wrap = false
}: Props) => {
  const { modalState, toggleModal, closeModel } = useModalManager<'history'>(['history'])

  const { data: history, isLoading } = useQuery({
    queryKey: historyQueryKey,
    queryFn: getHistory,
    enabled: modalState.history
  })

  const latest = reminders?.at(-1)
  if (!latest) return '-'

  return (
    <div className="py-2">
      <Badge
        variant={getStaffReminderVariant(latest)}
        text={getStaffReminderLabel(latest, wrap)}
        className={
          wrap ? 'h-auto min-h-7 py-1 whitespace-normal cursor-pointer' : 'cursor-pointer'
        }
        onClick={() => toggleModal('history')}
      />

      {/* the popup is rendered in a portal: keep its clicks away from the row */}
      <div onClick={(e) => e.stopPropagation()}>
        <Modal
          icon={BellIcon}
          size={'medium'}
          hideActions
          isOpen={modalState.history}
          onCancel={() => closeModel('history')}
          title={historyTitles[type]}
        >
          {isLoading ? (
            <Loader center />
          ) : (
            <div className="grid grid-cols-[auto_auto_auto_1fr] gap-x-6 gap-y-2 items-center">
              <span className="font-semibold">N°</span>
              <span className="font-semibold">Date</span>
              <span className="font-semibold">Type</span>
              <span className="font-semibold">Envoyée par</span>
              {[...(history?.[type] ?? [])].reverse().map((reminder) => (
                <div key={`${reminder.index}-${String(reminder.sentAt)}`} className="contents">
                  <Badge variant={getStaffReminderVariant(reminder)} text={`R${reminder.index}`} />
                  <span>{DateFormatter.DDMMYYYY(reminder.sentAt)}</span>
                  <span>{getStaffReminderTypeLabel(reminder)}</span>
                  <span>{getStaffReminderSenderLabel(reminder)}</span>
                </div>
              ))}
            </div>
          )}
        </Modal>
      </div>
    </div>
  )
}

export default StaffLatestReminderCell
